import { Component, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { DynamicFilterConfig, createDynamicFilterState } from '@card-software/library';
import { TranslateService } from '@ngx-translate/core';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import _ from 'lodash';
import {
  ExpenseClaim,
  ExpenseClaimListScope,
  ExpenseClaimSearchCommand,
  ExpenseClaimStatus,
  ExpenseClaimSummary,
} from '../../../models/expense-claim/expense-claim';
import { ReceiptOwnerCode } from '../../../models/expense-receipt/expense-receipt';
import { ExpenseClaimProxyService } from '../../../proxies/expense-claim.proxy.service';
import { Currency, currencySymbol } from '../../../utilities/currency.util';
import { EXPENSE_CLAIM_STATUSES, expenseClaimStatusLabel, expenseClaimStatusSeverity } from '../expense-claim-status.util';

/** The filter panel's controls; select values arrive as strings. */
interface ExpenseClaimFilterFormModel {
  search: string | null;
  status: string | null;
  ownerCode: string | null;
}

type ExpenseClaimFilters = Partial<ExpenseClaimFilterFormModel>;

/**
 * Expense claims (Rendición de gastos, #5800). Tabs: every claim the caller may see, their own (created, or whose
 * fund or trip they hold), and the claims in approval they can decide now. New claims start from a fund or trip.
 */
@Component({
  selector: 'app-expense-claims',
  templateUrl: './expense-claims.component.html',
})
export class ExpenseClaimsComponent {
  private readonly proxy = inject(ExpenseClaimProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);

  private currentSearchCriteria: ExpenseClaimSearchCommand = {};

  readonly scopes = ExpenseClaimListScope;
  readonly ownerCodes = ReceiptOwnerCode;

  readonly isLoading = signal(0);
  readonly showFilters = signal(true);
  readonly filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'expense-claim-filters', config: this.filterConfig });
  readonly items = signal<ExpenseClaim[]>([]);
  readonly summary = signal<ExpenseClaimSummary | null>(null);
  readonly scope = signal<ExpenseClaimListScope>(ExpenseClaimListScope.All);
  readonly totalRecords = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = signal(25);
  readonly sortField = signal<string | null>(null);
  readonly sortOrder = signal(-1);

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters as ExpenseClaimFilters));

    effect(
      () => {
        if (this.translateService.currentLang) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
    this.loadSummary();
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  private t(key: string, fallback: string): string {
    const value = this.translateService.instant(key);
    return value === key ? fallback : value;
  }

  private tryInitializeFilterConfig(): void {
    this.filterConfig.set(this.buildFilterConfig());
  }

  private buildFilterConfig(): DynamicFilterConfig {
    return {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.t('ExpenseClaim_Filter_Search', 'Number, fund or trip'),
          placeholder: this.t('ExpenseClaim_Filter_Search_Placeholder', 'Search by number, fund, trip or description'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'ownerCode',
          label: this.t('ExpenseClaim_Filter_Type', 'Type'),
          placeholder: this.t('ExpenseClaim_Filter_Type_Placeholder', 'Fund or trip'),
          options: [
            { label: this.t('ExpenseClaim_Type_Fund', 'Petty cash fund'), value: String(ReceiptOwnerCode.PettyCashFund) },
            { label: this.t('ExpenseClaim_Type_Trip', 'Trip'), value: String(ReceiptOwnerCode.Trip) },
          ],
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'status',
          label: this.t('Status', 'Status'),
          placeholder: this.t('Select_status', 'Select status'),
          options: EXPENSE_CLAIM_STATUSES.map((status) => ({ label: this.statusLabel(status), value: String(status) })),
          showClear: true,
          defaultVisible: true,
        },
      ],
    };
  }

  private searchWithFilters(filters: ExpenseClaimFilters): void {
    const search = (filters.search ?? '').toString().trim();
    const status = _.isNil(filters.status) || filters.status === '' ? null : (Number(filters.status) as ExpenseClaimStatus);
    const ownerCode = _.isNil(filters.ownerCode) || filters.ownerCode === '' ? null : Number(filters.ownerCode);
    this.currentSearchCriteria = { search: search || null, statuses: status === null ? null : [status], ownerCode };
    this.currentPage.set(1);
    this.search();
  }

  /** p-tabs emits the tab's value untyped (string | number); the tabs only carry ExpenseClaimListScope values. */
  setScope(value: string | number | undefined): void {
    const scope = Number(value) as ExpenseClaimListScope;
    if (_.isNil(value) || scope === this.scope()) return;
    this.scope.set(scope);
    this.currentPage.set(1);
    this.search();
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.pageSize.set(event.rows ?? this.pageSize());
    this.search();
  }

  onSort(event: SortEvent): void {
    const newField = event.field ?? null;
    const newOrder = event.order ?? 1;
    if (newField === this.sortField() && newOrder === this.sortOrder()) return;
    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.search();
  }

  private search(): void {
    this.startLoading();
    this.proxy
      .search(
        { ...this.currentSearchCriteria, scope: this.scope() },
        this.currentPage(),
        this.pageSize(),
        this.sortField(),
        this.sortOrder(),
      )
      .pipe(finalize(() => this.stopLoading()))
      .subscribe((response) => {
        this.items.set(response.data?.items ?? []);
        this.totalRecords.set(response.data?.totalCount ?? 0);
      });
  }

  private loadSummary(): void {
    this.proxy.getSummary().subscribe((response) => this.summary.set(response.data ?? null));
  }

  redirectToDetails(id: string): void {
    this.router.navigate(['/expense-claim-details'], { queryParams: { id } });
  }

  createClaim(): void {
    this.redirectToDetails('new');
  }

  statusLabel(status: ExpenseClaimStatus): string {
    return expenseClaimStatusLabel(status, (k, f) => this.t(k, f));
  }

  statusSeverity(status: ExpenseClaimStatus): 'success' | 'secondary' | 'warn' | 'info' | 'danger' {
    return expenseClaimStatusSeverity(status);
  }

  symbolOf(currency: Currency): string {
    return currencySymbol(currency);
  }

  getRow(rowData: unknown): ExpenseClaim {
    return rowData as ExpenseClaim;
  }
}
